import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import { Loader2, ClipboardCheck, CheckCircle2, XCircle } from "lucide-react";
import { UniHeader } from "./_lmsShared";

export default function UniversityQuizPage() {
  const { quizId } = useParams();
  const [params] = useSearchParams();
  const enrollmentId = params.get("enrollment_id");
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!enrollmentId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await universityApi.lmsQuiz(quizId, enrollmentId);
        setData(r.data);
      } catch (e) {
        toast.error(formatApiError(e, "Could not load quiz"));
      } finally {
        setLoading(false);
      }
    })();
  }, [quizId, enrollmentId]);

  const setAns = (qid, value) => setAnswers((s) => ({ ...s, [qid]: value }));

  const onSubmit = async () => {
    if (!enrollmentId) return;
    setSubmitting(true);
    try {
      const payload = (data?.quiz?.questions || []).map((q) => ({
        question_id: q.id,
        value: answers[q.id],
      }));
      const r = await universityApi.lmsQuizSubmit(quizId, enrollmentId, payload);
      setResult(r.data);
      if (r.data.passed) toast.success(`Passed! Score ${r.data.score}%`);
      else toast.error(`Score ${r.data.score}% — needs ${r.data.passing_score}% to pass`);
      if (r.data.course_completed) toast.success("Course complete! Credential will be issued shortly.");
    } catch (e) {
      toast.error(formatApiError(e, "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!enrollmentId) return <div className="min-h-screen bg-[#FBF8F2] p-10">Missing enrollment_id parameter.</div>;
  if (loading) return <div className="min-h-screen bg-[#FBF8F2] p-10 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading quiz…</div>;
  if (!data) return <div className="min-h-screen bg-[#FBF8F2] p-10">Quiz not available.</div>;

  const q = data.quiz;

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="Quiz" back="/university/student" />
      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="uni-quiz-page">
        <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-500 mb-1">
          <ClipboardCheck className="w-3.5 h-3.5" /><span className="font-mono">{q.course_code}</span> · {q.quiz_type}
        </div>
        <h1 className="font-display text-3xl text-slate-900" data-testid="uni-quiz-title">{q.title}</h1>
        <div className="mt-1 text-sm text-slate-600">Passing score: {q.passing_score}%</div>

        {result && (
          <div className={`mt-5 rounded-2xl border p-5 ${result.passed ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid="uni-quiz-result">
            <div className="flex items-center gap-2 text-lg">
              {result.passed ? <CheckCircle2 className="w-5 h-5 text-emerald-700" /> : <XCircle className="w-5 h-5 text-rose-700" />}
              <span className={`font-display ${result.passed ? "text-emerald-900" : "text-rose-900"}`}>
                {result.passed ? "You passed!" : "Did not pass — try again"}
              </span>
            </div>
            <div className="mt-1 text-sm">
              Score: <strong>{result.score}%</strong> ({result.earned}/{result.total_points} points)
            </div>
          </div>
        )}

        <div className="mt-6 space-y-4">
          {(q.questions || []).map((qq, idx) => (
            <section key={qq.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`uni-quiz-q-${qq.id}`}>
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Question {idx + 1} · {qq.kind.replace("_", " ")}</div>
              <div className="font-medium text-slate-900 mb-3">{qq.prompt}</div>
              {qq.kind === "short_answer" ? (
                <input
                  type="text"
                  value={answers[qq.id] || ""}
                  onChange={(e) => setAns(qq.id, e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 h-10 text-sm focus:ring-2 focus:ring-purple-100"
                  disabled={!!result}
                  data-testid={`uni-quiz-input-${qq.id}`}
                />
              ) : (
                <div className="space-y-2">
                  {(qq.options || []).map((o, i) => (
                    <label key={i} className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${
                      Number(answers[qq.id]) === i ? "border-purple-700 bg-purple-50/40" : "border-slate-200"
                    } ${result ? "opacity-80 cursor-not-allowed" : ""}`}>
                      <input
                        type="radio"
                        name={qq.id}
                        checked={Number(answers[qq.id]) === i}
                        onChange={() => setAns(qq.id, i)}
                        disabled={!!result}
                        data-testid={`uni-quiz-opt-${qq.id}-${i}`}
                      />
                      <span className="text-sm text-slate-900">{o.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>

        {!result && (
          <div className="mt-6">
            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium px-4 h-10 disabled:opacity-60"
              data-testid="uni-quiz-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
              Submit quiz
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
